import type { CSSProperties } from "react";
import { BsMessenger, BsWhatsapp } from "react-icons/bs";
import { askingAgain, doneBuying } from "./customers";
import "./labels.css";

/**
 * Which network a chat lives on, on its face's corner (prototype core.js Avatar .net): WhatsApp's green or Messenger's
 * blue — with the network's own mark in it. A plain dot read as 「在线」, and with 全部 picked nobody could tell the
 * two apart (owner 10/25).
 */
export function NetDot({ fb }: { fb: boolean }) {
  return (
    <i className={`wc-av-net${fb ? " fb" : ""}`} title={fb ? "Messenger" : "WhatsApp"}>
      {fb ? <BsMessenger /> : <BsWhatsapp />}
    </i>
  );
}

// The colleagues' WhatsApp labels — 谷粮, 排毒, Inquiry｜Day 1–7, Consider,
// Pending Payment, NURTURE — in their phone's own colours (read from the
// shop's WhatsApp Business, 2026-10-13), so a customer here wears the same
// label they wear on the phone. The system works each one out: the product
// from the chat, the day from when they first wrote, the rest from 进度 — and
// for a buyer who tapped another ad since (askingAgain), the product of that
// ad and the day from the tap.

/** A label as the phone shows it: its words and its colour. */
export interface WaLabel {
  key: string;
  text: string;
  rgb: string;
}

const PRODUCTS: Record<string, { text: string; rgb: string }> = {
  grain: { text: "谷粮", rgb: "255,46,116" },
  detox: { text: "排毒", rgb: "203,41,16" },
  sleep: { text: "Zenite", rgb: "176,140,82" },
  "2hers": { text: "代餐", rgb: "234,0,56" },
};

/** The conversation's product key (playbook) as the phone's product label. */
const PRODUCT_OF: Record<string, string> = {
  mayterlynn: "grain",
  detox: "detox",
  cherryberry: "detox",
  zenite: "sleep",
  "2hers": "2hers",
};

export function productLabel(product: string): WaLabel | null {
  const k = PRODUCT_OF[product] ?? product;
  const p = PRODUCTS[k];
  return p ? { key: k, ...p } : null;
}

/** What an ad sells (the API's ad_product, by the campaign) as the phone's product label. */
const AD_PRODUCT_OF: Record<string, string> = { cereal: "grain", detox: "detox", meal: "2hers", zenite: "sleep" };

/** A product as 经营 keys it (cereal, zenite, detox, meal) as the phone's product label; none for other. */
export function adProductLabel(key: string): WaLabel | null {
  const k = AD_PRODUCT_OF[key];
  return k ? productLabel(k) : null;
}

/**
 * A chat's product label: its script's product — or, for a buyer asking
 * again, the product of the ad they tapped (the script's is what they bought
 * before); none when that ad sells nothing the phone has a label for.
 */
export function leadProduct(l: { product?: string; ad_product?: string; ad_clicked_at?: string; last_order_at?: string }): WaLabel | null {
  if (askingAgain(l)) {
    const k = AD_PRODUCT_OF[l.ad_product ?? ""];
    return k ? productLabel(k) : null;
  }
  return productLabel(l.product ?? "");
}

/** A product as the follow-up page names it (followup_board.go's product_label). */
export function productLabelByName(name: string): WaLabel {
  for (const [key, p] of Object.entries(PRODUCTS)) if (p.text === name) return { key, ...p };
  return { key: name, text: name, rgb: "134,150,160" };
}

const STAGES: Record<string, { text: string; rgb: string }> = {
  day1: { text: "Inquiry｜Day 1", rgb: "127,102,255" },
  day2: { text: "Inquiry｜Day 2", rgb: "209,196,255" },
  day3: { text: "Inquiry｜Day 3", rgb: "106,108,108" },
  day4: { text: "Inquiry｜Day 4", rgb: "255,185,56" },
  day5: { text: "Inquiry｜Day 5", rgb: "221,207,188" },
  day6: { text: "Inquiry｜Day 6", rgb: "175,233,102" },
  day7: { text: "Inquiry｜Day 7", rgb: "138,150,46" },
  consider: { text: "Consider", rgb: "250,101,51" },
  pay: { text: "Pending Payment", rgb: "255,185,56" },
  nurture: { text: "NURTURE", rgb: "182,217,254" },
  order: { text: "Order Confirmed", rgb: "192,131,93" },
  dealer: { text: "Dealer customer", rgb: "193,90,221" },
};

export function stageLabel(key: string): WaLabel | null {
  const s = STAGES[key];
  return s ? { key, ...s } : null;
}

/** The Malaysian calendar day of an instant, as days since the epoch. */
function mytDay(ms: number): number {
  return Math.floor((ms + 8 * 3_600_000) / 86_400_000);
}

/**
 * A customer's stage label from what the chat list knows: bought (and not
 * asking again since), a dealer, waiting to pay, considering, else the day
 * since they first wrote — a buyer asking again, since the tap — (Day 1 that
 * day) for a week and NURTURE after. Null when they are closed. It read
 * Order Confirmed for good after a first sale, so a 谷粮 buyer asking about
 * 排毒 looked finished (owner, 2026-10-19).
 */
export function leadStage(
  l: { status?: string; purchased?: boolean; first_seen_at?: string | null; ad_clicked_at?: string; last_order_at?: string },
  now = Date.now(),
): WaLabel | null {
  if (l.status === "won" || doneBuying(l)) return stageLabel("order");
  switch (l.status) {
    case "agent":
      return stageLabel("dealer");
    case "awaiting_payment":
      return stageLabel("pay");
    case "considering":
      return stageLabel("consider");
    case "cancelled":
    case "invalid":
    case "lost":
      return null;
  }
  const from = askingAgain(l) ? l.ad_clicked_at : l.first_seen_at;
  if (!from) return null;
  const day = mytDay(now) - mytDay(Date.parse(from)) + 1;
  return stageLabel(day <= 7 ? `day${Math.max(1, day)}` : "nurture");
}

/** "10/1": an instant's Malaysian day. */
function md(iso: string): string {
  const d = new Date(Date.parse(iso) + 8 * 3_600_000);
  return `${d.getUTCMonth() + 1}/${d.getUTCDate()}`;
}

/** "RM158", "RM6.90". */
const rm = (sen: number) =>
  "RM" + (sen / 100).toLocaleString("en-US", { minimumFractionDigits: sen % 100 ? 2 : 0, maximumFractionDigits: 2 });

type Before = {
  product?: string;
  ad_product?: string;
  ad_clicked_at?: string;
  last_order_at?: string;
  last_sold?: { at: string; items?: string; sen: number };
};

/** "10/1 买过 紫薯谷粮×2 RM158": what a buyer asking again bought before. */
export function boughtBefore(l: Before): string {
  const s = l.last_sold;
  const at = s?.at || l.last_order_at;
  return [at ? `${md(at)} 买过` : "买过", s?.items, s?.sen ? rm(s.sen) : ""].filter(Boolean).join(" ");
}

/**
 * "回头客 · 10/1 买过 紫薯谷粮×2 RM158 · 10/18 点了排毒广告又来问": the
 * drawer's line for a buyer asking again, where a phone has no hover to show
 * the old label's words. "" for anybody else.
 */
export function askingAgainLine(l: Before): string {
  if (!askingAgain(l) || !l.ad_clicked_at) return "";
  return `回头客 · ${boughtBefore(l)} · ${md(l.ad_clicked_at)} 点了${leadProduct(l)?.text ?? ""}广告又来问`;
}

/**
 * The labels a buyer asking again wore before the tap — the product they
 * bought and Order Confirmed — as one faded label after the new ones, what
 * they bought on it (owner, 2026-10-19: 「旧的还是要有地方看」). Faded, so
 * it does not read as where they are now: Order Confirmed on a new enquiry
 * was the complaint. Null for anybody else.
 */
export function pastLabel(l: Before): { label: WaLabel; title: string } | null {
  if (!askingAgain(l)) return null;
  const order = stageLabel("order")!;
  const product = productLabel(l.product ?? "");
  return {
    label: { key: "past", text: product ? `${product.text} · ${order.text}` : order.text, rgb: order.rgb },
    title: `以前：${boughtBefore(l)}`,
  };
}

/** One label, drawn as the phone draws it; past, faded (pastLabel). */
export function LabelChip({ l, title, past }: { l: WaLabel; title?: string; past?: boolean }) {
  return (
    <span className={`wa-label${past ? " past" : ""}`} style={{ "--lc": `rgb(${l.rgb})` } as CSSProperties} title={title}>
      {l.text}
    </span>
  );
}

/** The dot a label wears in a list, as the phone's label list draws it. */
export function LabelDot({ l }: { l: WaLabel }) {
  return <i className="wl-dot" style={{ "--lc": `rgb(${l.rgb})` } as CSSProperties} aria-hidden="true" />;
}
